import { useEffect, useState } from "react";
import { BaseRequest } from "../base/BaseRequest";
import { HttpRequest } from "../base/HttpModels";
import { DISH_PUT, DISH_DELETE, CATEGORY_PUT, CATEGORY_DELETE, MENU_LIST } from "../menu/menuActions";
import { getMenuList, putDish, deleteDish, putCategory, deleteCategory } from "../menu/menuSlice";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { 
  IDishGroup, 
  IDishPutRequest, 
  IDishDeleteRequest, 
  ICategoryPutRequest, 
  ICategoryDeleteRequest, 
  Dish 
} from "../menu/menuTypes";
import withAuthentication from "../base/AuthComponent";
import DrawerAppBarHOC from "../home/DrawerAppBar";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { isMobile } from "react-device-detect";
import React from "react";

interface DishFormData {
  [dishId: number]: {
    name: string;
    price: number;
    is_take_away: boolean;
    is_enabled: boolean;
    category_id: number;
  };
}

interface NewDishFormData {
  name: string;
  price: number;
  is_take_away: boolean;
  is_enabled: boolean;
}

interface NewCategoryFormData {
  name: string;
  position: number;
}

// DishCard component: manages its own local state
interface DishCardProps {
  dish: Dish;
  formData: DishFormData[number];
  menu: IDishGroup[];
  onSave: (dishId: number, data: DishFormData[number]) => void;
  onReset: () => void;
  onDelete: () => void;
}

const DishCard = React.memo(({ dish, formData, menu, onSave, onReset, onDelete }: DishCardProps) => {
  const [localData, setLocalData] = useState(formData);
  const theme = useTheme();
  
  useEffect(() => {
    setLocalData(formData);
  }, [formData]);

  return (
    <Card 
      key={dish.id}
      variant="outlined" 
      sx={{ 
        width: "100%", 
        marginBottom: 2,
        backgroundColor: localData.is_enabled 
          ? theme.palette.background.paper 
          : theme.palette.action.disabledBackground
      }}
    >
      <CardContent sx={{ padding: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Piatto ID: {dish.id}
              </Typography>
              <IconButton
                color="error"
                onClick={onDelete}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome Piatto"
              variant="outlined"
              value={localData.name}
              onChange={e => setLocalData(d => ({ ...d, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Prezzo (€)"
              variant="outlined"
              type="number"
              inputProps={{ step: "0.50", min: "0" }}
              value={localData.price}
              onChange={e => setLocalData(d => ({ ...d, price: parseFloat(e.target.value) || 0 }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Categoria</InputLabel>
              <Select
                value={localData.category_id}
                onChange={e => setLocalData(d => ({ ...d, category_id: e.target.value as number }))}
                label="Categoria"
              >
                {menu.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={localData.is_take_away}
                  onChange={e => setLocalData(d => ({ ...d, is_take_away: e.target.checked }))}
                  color="primary"
                />
              }
              label="Solo asporto"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={localData.is_enabled}
                  onChange={e => setLocalData(d => ({ ...d, is_enabled: e.target.checked }))}
                  color="primary"
                />
              }
              label="Piatto abilitato"
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={onReset}
              >
                Annulla
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => onSave(dish.id, localData)}
              >
                Salva Modifiche
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
});

function ManageMenuComponentBody() {
  const menu = useAppSelector((state) => state.menuState.menu);
  const token = useAppSelector((state) => state.loginState.token);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  
  // State to manage form data for all dishes
  const [dishFormData, setDishFormData] = useState<DishFormData>({});
  
  // State for new dish forms (one per category)
  const [newDishForms, setNewDishForms] = useState<{ [categoryId: number]: NewDishFormData }>({});
  
  // State for new category form
  const [newCategoryForm, setNewCategoryForm] = useState<NewCategoryFormData>({
    name: "",
    position: 0
  });
  
  // State for delete confirmation dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'dish' | 'category', id: number, name: string } | null>(null);
  
  // Initialize form data when menu loads
  useEffect(() => {
    if (menu && menu.length > 0) {
      const initialFormData: DishFormData = {};
      const initialNewDishForms: { [categoryId: number]: NewDishFormData } = {};
      
      menu.forEach((group: IDishGroup) => {
        // Initialize new dish form for each category
        initialNewDishForms[group.id] = {
          name: "",
          price: 0,
          is_take_away: false,
          is_enabled: true,
        };
        
        // Initialize existing dish forms
        group.dishes.forEach((dish: Dish) => {
          initialFormData[dish.id] = {
            name: dish.name,
            price: dish.price,
            is_take_away: Boolean(dish.is_take_away),
            is_enabled: Boolean(dish.is_enabled),
            category_id: dish.category_id || group.id, // Use group.id as fallback
          };
        });
      });
      
      setDishFormData(initialFormData);
      setNewDishForms(initialNewDishForms);
      
      // Set next position for new category
      const maxPosition = menu.reduce((max, category) => Math.max(max, category.id), 0);
      setNewCategoryForm(prev => ({ ...prev, position: maxPosition + 1 }));
    }
  }, [menu]);

  useEffect(() => {
    if (token === "" || token === undefined) {
      return;
    }
    const menuRequest: HttpRequest<BaseRequest> = {
      action: MENU_LIST,
      parameters: {
        user_token: token,
      },
    };
    dispatch(getMenuList(menuRequest));
  }, [token, dispatch]);

  // Handle new dish form changes
  const handleNewDishChange = (categoryId: number, field: keyof NewDishFormData, value: string | number | boolean) => {
    setNewDishForms(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [field]: value
      }
    }));
  };

  // Handle new category form changes
  const handleNewCategoryChange = (field: keyof NewCategoryFormData, value: string | number) => {
    setNewCategoryForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle saving dish changes
  const handleSaveChanges = (dish: Dish, formData: DishFormData[number]) => {
    if (!formData || !token) return;
  
    const request: HttpRequest<IDishPutRequest> = {
      action: DISH_PUT,
      parameters: {
        user_token: token,
        dish_id: dish.id,
        name: formData.name,
        price: formData.price,
        category_id: formData.category_id,
        is_take_away: Boolean(formData.is_take_away),
        is_enabled: Boolean(formData.is_enabled),
      },
    };
    dispatch(putDish(request));
  };

  // Handle creating new dish
  const handleCreateDish = (categoryId: number) => {
    const formData = newDishForms[categoryId];
    if (!formData || !token || !formData.name.trim()) return;

    const request: HttpRequest<IDishPutRequest> = {
      action: DISH_PUT,
      parameters: {
        user_token: token,
        name: formData.name,
        price: formData.price,
        category_id: categoryId,
        is_take_away: Boolean(formData.is_take_away),
        is_enabled: formData.is_enabled,
      },
    };
    dispatch(putDish(request));

    // Reset form
    setNewDishForms(prev => ({
      ...prev,
      [categoryId]: {
        name: "",
        price: 0,
        is_take_away: false,
        is_enabled: true,
      }
    }));
  };

  // Handle creating new category
  const handleCreateCategory = () => {
    if (!token || !newCategoryForm.name.trim()) return;

    const request: HttpRequest<ICategoryPutRequest> = {
      action: CATEGORY_PUT,
      parameters: {
        user_token: token,
        name: newCategoryForm.name,
        position: newCategoryForm.position,
      },
    };
    dispatch(putCategory(request));

    // Reset form
    setNewCategoryForm({
      name: "",
      position: 0
    });
  };

  // Handle delete confirmation
  const handleDeleteClick = (type: 'dish' | 'category', id: number, name: string) => {
    setDeleteTarget({ type, id, name });
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (!deleteTarget || !token) return;

    if (deleteTarget.type === 'dish') {
      const request: HttpRequest<IDishDeleteRequest> = {
        action: DISH_DELETE,
        parameters: {
          user_token: token,
          dish_id: deleteTarget.id,
        },
      };
      dispatch(deleteDish(request));
    } else if (deleteTarget.type === 'category') {
      const request: HttpRequest<ICategoryDeleteRequest> = {
        action: CATEGORY_DELETE,
        parameters: {
          user_token: token,
          category_id: deleteTarget.id,
        },
      };
      dispatch(deleteCategory(request));
    }

    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  return (
    <Box
      key={"menu_root"}
      sx={{
        width: "100%",
        paddingLeft: isMobile ? "2%" : "25%",
        paddingRight: isMobile ? "2%" : "25%",
        display: "block",
        alignItems: "left",
      }}
    >
      {menu.map((group: IDishGroup) => (
        <Accordion key={group.id} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
                {group.name}
              </Typography>
              <IconButton
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick('category', group.id, group.name);
                }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {group.dishes.map((dish) => {
                const formData = dishFormData[dish.id];
                if (!formData) return null;
                return (
                  <DishCard
                    key={dish.id}
                    dish={dish}
                    formData={formData}
                    menu={menu}
                    onSave={(dishId, data) => {
                      setDishFormData(prev => ({ ...prev, [dishId]: data }));
                      handleSaveChanges(dish, data);
                    }}
                    onReset={() => setDishFormData(prev => ({
                      ...prev,
                      [dish.id]: {
                        name: dish.name,
                        price: dish.price,
                        category_id: dish.category_id || group.id,
                        is_take_away: Boolean(dish.is_take_away),
                        is_enabled: Boolean(dish.is_enabled),
                      }
                    }))}
                    onDelete={() => handleDeleteClick('dish', dish.id, dish.name)}
                  />
                );
              })}
              
              {/* New Dish Form */}
              <Card 
                variant="outlined" 
                sx={{ 
                  width: "100%", 
                  marginBottom: 2,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? theme.palette.grey[800] 
                    : theme.palette.grey[50],
                  border: `2px dashed ${theme.palette.divider}`
                }}
              >
                <CardContent sx={{ padding: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom color="primary">
                        <AddIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Nuovo Piatto
                      </Typography>
                    </Grid>
                    
                    {/* New Dish Name Field */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nome Piatto"
                        variant="outlined"
                        value={newDishForms[group.id]?.name || ""}
                        onChange={(e) => handleNewDishChange(group.id, 'name', e.target.value)}
                      />
                    </Grid>
                    
                    {/* New Dish Price Field */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Prezzo (€)"
                        variant="outlined"
                        type="number"
                        inputProps={{ 
                          step: "0.50", 
                          min: "0" 
                        }}
                        value={newDishForms[group.id]?.price || 0}
                        onChange={(e) => handleNewDishChange(group.id, 'price', parseFloat(e.target.value) || 0)}
                      />
                    </Grid>
                    
                    {/* New Dish Take Away Switch */}
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={newDishForms[group.id]?.is_take_away || false}
                            onChange={(e) => handleNewDishChange(group.id, 'is_take_away', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Solo asporto"
                      />
                    </Grid>
                    
                    {/* New Dish Enabled Switch */}
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={newDishForms[group.id]?.is_enabled ?? true}
                            onChange={(e) => handleNewDishChange(group.id, 'is_enabled', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Piatto abilitato"
                      />
                    </Grid>
                    
                    {/* New Dish Action Buttons */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setNewDishForms(prev => ({
                              ...prev,
                              [group.id]: {
                                name: "",
                                price: 0,
                                is_take_away: false,
                                is_enabled: true,
                              }
                            }));
                          }}
                        >
                          Annulla
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleCreateDish(group.id)}
                          disabled={!newDishForms[group.id]?.name?.trim()}
                        >
                          Crea Piatto
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
      
      {/* New Category Form */}
      <Card 
        variant="outlined" 
        sx={{ 
          width: "100%", 
          marginTop: 3,
          backgroundColor: theme.palette.mode === 'dark' 
            ? theme.palette.success.dark 
            : theme.palette.success.light,
          border: `2px dashed ${theme.palette.success.main}`
        }}
      >
        <CardContent sx={{ padding: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom color="primary">
                <AddIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Nuova Categoria
              </Typography>
            </Grid>
            
            {/* New Category Name Field */}
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Nome Categoria"
                variant="outlined"
                value={newCategoryForm.name}
                onChange={(e) => handleNewCategoryChange('name', e.target.value)}
              />
            </Grid>
            
            {/* New Category Position Field */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Posizione"
                variant="outlined"
                type="number"
                inputProps={{ min: "0" }}
                value={newCategoryForm.position}
                onChange={(e) => handleNewCategoryChange('position', parseInt(e.target.value) || 0)}
              />
            </Grid>
            
            {/* New Category Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setNewCategoryForm({
                      name: "",
                      position: 0
                    });
                  }}
                >
                  Annulla
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateCategory}
                  disabled={!newCategoryForm.name.trim()}
                >
                  Crea Categoria
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Conferma Eliminazione
        </DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare {deleteTarget?.type === 'dish' ? 'il piatto' : 'la categoria'} "{deleteTarget?.name}"?
            {deleteTarget?.type === 'category' && (
              <><br /><strong>Attenzione:</strong> Tutti i piatti in questa categoria verranno rimossi.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ManageMenuComponent() {
  return (
    <DrawerAppBarHOC
      title="Gestisci Menu"
      content={<ManageMenuComponentBody />}
    />
  );
}

export default withAuthentication(ManageMenuComponent);
